import { Entities_GymData } from '@suntex.fim/api-client';
import {
  $ArithmeticOperation,
  $GymMode,
  ArithmeticOperation,
  GymMode,
  SlugFor,
} from '@suntex.fim/enums';

export function arithmeticOperationsByGymMode(
  gymMode: GymMode,
): ArithmeticOperation[] {
  //get the values from the enum
  const values = $ArithmeticOperation.describeAll().map((d) => d.value);

  //if K2
  if (gymMode === GymMode.K2) {
    //filter to just addition and subtraction
    return values.filter(
      (x) =>
        x === ArithmeticOperation.Addition ||
        x === ArithmeticOperation.Subtraction,
    );
  }

  //return
  return values;
}

export const arithmeticOperationSlugs = $ArithmeticOperation
  .describeAll()
  .map((op) => op.slug);

export type ArithmeticOperationSlug = SlugFor<typeof $ArithmeticOperation>;

/**
 * Returns column keys for a specific gym level.
 */
export function getGymColumnKeys(gymMode: GymMode): ArithmeticOperationSlug[] {
  return $ArithmeticOperation
    .describeAll(arithmeticOperationsByGymMode(gymMode))
    .map((op) => op.slug);
}

/**
 * Resolves a player's gym data for the selected mode.
 *
 * `gymData` is parallel to `gymModes` (one entry per mode the response includes,
 * same order), not indexed by GymMode value. When only some modes are returned
 * (e.g. K-2 returns K2), indexing by value surfaces the wrong mode's data,
 * so match the selected mode against `gymModes` to find its position.
 */
export function getPlayerGymDataForMode(
  gymData: Entities_GymData[] | undefined,
  gymModes: GymMode[] | undefined,
  mode: GymMode,
): Entities_GymData | undefined {
  if (!gymData || !gymModes) return undefined;

  // gymModes may arrive as slugs at runtime though typed as GymMode values.
  const slug = $GymMode.describe(mode).slug;
  const NOT_FOUND = -1;
  const index = gymModes.findIndex((m) => m === mode || String(m) === slug);

  return index === NOT_FOUND ? undefined : gymData[index];
}

/**
 * Builds the gym level-select options for the modes the report actually covers.
 *
 * Only the modes present in `gymModes` have data, so the selector must offer just
 * those (e.g. a K-2 team offers only K2). Reported order is preserved. Falls back
 * to every mode when `gymModes` is missing or empty.
 */
export function getGymModeOptions(
  gymModes: GymMode[] | undefined,
): { key: GymMode; label: string }[] {
  const descriptors = $GymMode.describeAll();

  // No modes reported: offer every mode.
  if (!gymModes || !gymModes.length) {
    return descriptors.map((descriptor) => ({
      key: descriptor.value,
      label: descriptor.label,
    }));
  }

  // gymModes may arrive as slugs at runtime though typed as GymMode values, so
  // match each entry to its descriptor by value or slug. An unrecognized entry is
  // logged and skipped rather than thrown, so bad data can't blank the report.
  return gymModes.flatMap((mode) => {
    const descriptor = descriptors.find(
      (candidate) =>
        candidate.value === mode || candidate.slug === String(mode),
    );

    if (!descriptor) {
      // eslint-disable-next-line no-console
      console.error(`Unrecognized gym mode in report data: ${String(mode)}`);
      return [];
    }

    return [{ key: descriptor.value, label: descriptor.label }];
  });
}
